import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react'

import {
  api,
  type AwsAccountOutput,
  type AwsConnectionOutput,
  type AwsConnectionPayload,
  type AwsPlatformIdentity,
  type ConnMethod,
} from '../../api/client'
import type { AwsSsoConnectionApi, AwsSsoResolvedAccount, CredentialTestResult } from '../../api/awsSso'
import { AwsAccessGuide } from './AwsAccessGuide'
import { AwsAssumeRoleSetupHelp } from './AwsAssumeRoleSetupHelp'
import { AwsIamSetupHelp } from './AwsIamSetupHelp'
import { AwsOrgIamSetupHelp } from './AwsOrgIamSetupHelp'
import { AwsSsoConnectionWizard } from './AwsSsoConnectionWizard'

/** The endpoints the editor calls — injectable so the portal and the public
 *  /newcustomer flow route them through their own transports. */
export interface AwsConnectionClient {
  saveAwsConnection: (slug: string, payload: AwsConnectionPayload) => Promise<unknown>
  testAwsCredentials: (
    slug: string,
    accessKey: string,
    secretKey: string,
    expectedAccountId?: string,
    sessionToken?: string,
    awsAccountId?: string,
  ) => Promise<CredentialTestResult>
  /** assume_role mode only. Optional so the public-onboarding client, which
   *  doesn't offer that method, still satisfies this interface. */
  testAwsAssumeRole?: (slug: string, roleArn: string, expectedAccountId?: string) => Promise<CredentialTestResult>
  getAwsPlatformIdentity?: (slug: string) => Promise<AwsPlatformIdentity>
}

/**
 * The "Connect" step of the AWS-import wizard: a credential form for ONE chosen
 * connection method. The wizard owns the method choice + step chrome; this
 * component only renders the fields for `method` and handles test + save.
 *
 *   - sso          → AWS IAM Identity Center sign-in (device flow) authorizes
 *                    durable read-only per-account keys; saved per_account.
 *   - per_account  → each account row carries its OWN access/secret key.
 *   - organization → one management/delegated-admin key; the importer assumes a
 *                    read-only role in each listed member account.
 *   - assume_role  → each account row carries a role ARN that we assume from
 *                    OUR OWN credentials with the company's platform-issued
 *                    ExternalId (shown read-only, never typed). The only
 *                    method where no customer secret is transmitted or stored.
 */

interface AccountDraft {
  aws_account_id: string
  name: string
  access_key: string
  secret_key: string
  session_key: string
  role_arn: string
  saved?: AwsAccountOutput
}

interface ConnectionDraft {
  org_name: string
  org_aws_id: string
  org_access_key: string
  org_secret_key: string
  org_session_key: string
  accounts: AccountDraft[]
}

function emptyAccount(): AccountDraft {
  return {
    aws_account_id: '', name: '', access_key: '', secret_key: '', session_key: '',
    role_arn: '',
  }
}

function draftFrom(connection: AwsConnectionOutput | null, method: ConnMethod): ConnectionDraft {
  const accounts: AccountDraft[] = (connection?.accounts ?? []).map((a) => ({
    aws_account_id: a.aws_account_id,
    name: a.name,
    access_key: '',
    secret_key: '',
    session_key: '',
    // The ARN isn't a secret, so it round-trips and stays editable.
    role_arn: a.role_arn ?? '',
    saved: a,
  }))
  // Show the account form straight away (no "Add account" click) for the key-
  // entry methods. SSO populates accounts from its sign-in flow instead.
  if (method !== 'sso' && accounts.length === 0) accounts.push(emptyAccount())
  return {
    org_name: connection?.org_name ?? '',
    org_aws_id: connection?.org_aws_id ?? '',
    org_access_key: '',
    org_secret_key: '',
    org_session_key: '',
    accounts,
  }
}

/** Whether the current draft is savable+scannable for its method. */
function isReady(draft: ConnectionDraft, method: ConnMethod, connection: AwsConnectionOutput | null): boolean {
  if (method === 'sso') return draft.accounts.length > 0
  // assume_role: a role ARN is the whole credential — there are no keys to check.
  if (method === 'assume_role') {
    return draft.accounts.some((a) => !!a.role_arn.trim() || !!a.saved?.role_arn)
  }
  if (method === 'organization') {
    const hasOrgKeys = (!!draft.org_access_key && !!draft.org_secret_key) || !!connection?.has_org_keys
    return draft.accounts.length > 0 && hasOrgKeys
  }
  // per_account: at least one account with keys (freshly typed or already saved).
  return draft.accounts.some((a) => (!!a.access_key && !!a.secret_key) || !!a.saved?.has_keys)
}

/** The company's ExternalId, read-only: the platform issues it, so the
 *  consultant can copy it to the customer but never edit it. */
function ExternalIdField({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
    } catch {
      /* clipboard blocked (e.g. insecure context) — the value is selectable */
    }
  }
  return (
    <div className="field-grid" style={{ marginTop: '0.75rem' }}>
      <label className="full">
        External ID (issued by the platform — the role's trust policy must require it)
        <span style={{ display: 'flex', gap: '0.5rem' }}>
          <input type="text" value={value} readOnly onFocus={(e) => e.currentTarget.select()} data-testid="aws-external-id" />
          <button type="button" onClick={copy} data-testid="aws-external-id-copy">{copied ? 'Copied' : 'Copy'}</button>
        </span>
      </label>
    </div>
  )
}

/** A credential test's progress/outcome line, green on OK. */
function TestResult({ text, size, testid }: { text: string | undefined; size: string; testid?: string }) {
  if (!text) return null
  return (
    <span style={{ fontSize: size, color: text.startsWith('OK') ? 'var(--ok)' : 'var(--danger)' }} data-testid={testid}>
      {text}
    </span>
  )
}

export interface AwsConnectionEditorHandle {
  /** Persist the connection. Returns true on success, false on failure (the
   *  editor surfaces its own error). Called by the wizard's Next button. */
  save: () => Promise<boolean>
}

interface Props {
  slug: string
  connection: AwsConnectionOutput | null
  method: ConnMethod
  onChanged: () => void | Promise<void>
  /** Reports whether the draft is savable, so the wizard can gate Next. */
  onReadyChange?: (ready: boolean) => void
  /** Incremented by the host when choosing SSO + Next should immediately open the SSO modal. */
  ssoAutoOpenSignal?: number
  /** Override the save/test endpoints (public onboarding). Defaults to `api`. */
  client?: AwsConnectionClient
  /** Override the SSO device-flow endpoints (AwsSsoConnectionWizard's `api`). */
  ssoApi?: AwsSsoConnectionApi
}

export const AwsConnectionEditor = forwardRef<AwsConnectionEditorHandle, Props>(
  function AwsConnectionEditor({ slug, connection, method, onChanged, onReadyChange, ssoAutoOpenSignal = 0, client = api, ssoApi }, ref) {
  const isOrg = method === 'organization'
  const isSso = method === 'sso'
  const isRole = method === 'assume_role'
  const [draft, setDraft] = useState<ConnectionDraft>(() => draftFrom(connection, method))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [orgTest, setOrgTest] = useState<string | null>(null)
  const [accountTests, setAccountTests] = useState<Record<number, string>>({})
  const [identity, setIdentity] = useState<AwsPlatformIdentity | null>(null)

  // The customer's trust policy names OUR account, so the setup help can't be
  // written until we know it. Only fetched for the method that needs it.
  const fetchIdentity = client.getAwsPlatformIdentity
  useEffect(() => {
    if (!isRole || !fetchIdentity) return
    let live = true
    fetchIdentity(slug)
      .then((r) => { if (live) setIdentity(r) })
      .catch(() => { if (live) setIdentity({ account_id: null, arn: null, reason: 'Could not read the platform identity.', external_id: null }) })
    return () => { live = false }
  }, [isRole, slug, fetchIdentity])

  // Re-derive from the server view when the method changes or the connection is
  // (re)loaded after a save. Stable during typing (connection ref unchanged).
  useEffect(() => { setDraft(draftFrom(connection, method)); setOrgTest(null); setAccountTests({}) }, [method, connection])

  // Keep the wizard's Next gate in sync with draft validity.
  useEffect(() => { onReadyChange?.(isReady(draft, method, connection)) }, [draft, method, connection, onReadyChange])

  const patchAccount = (idx: number, patch: Partial<AccountDraft>) => {
    setDraft((d) => ({ ...d, accounts: d.accounts.map((a, i) => (i === idx ? { ...a, ...patch } : a)) }))
  }

  const addSsoAccounts = (resolved: AwsSsoResolvedAccount[]) => {
    // SSO resolves keys, never roles.
    const rows: AccountDraft[] = resolved.map(({ account, credentials }) => ({
      aws_account_id: account.account_id,
      name: account.account_name,
      access_key: credentials.access_key_id,
      secret_key: credentials.secret_access_key,
      session_key: credentials.session_token,
      role_arn: '',
    }))
    const ids = new Set(rows.map((r) => r.aws_account_id))
    setDraft((d) => ({ ...d, accounts: [...d.accounts.filter((a) => !ids.has(a.aws_account_id)), ...rows] }))
  }

  const save = useCallback(async (): Promise<boolean> => {
    setBusy(true)
    setError(null)
    try {
      const payload: AwsConnectionPayload = {
        mode: isOrg ? 'organization' : isRole ? 'assume_role' : 'per_account',
        org_name: isOrg ? draft.org_name || null : null,
        org_aws_id: isOrg ? draft.org_aws_id || null : null,
        org_access_key: isOrg ? draft.org_access_key || null : null,
        org_secret_key: isOrg ? draft.org_secret_key || null : null,
        org_session_key: isOrg ? draft.org_session_key || null : null,
        accounts: draft.accounts.map((a) => ({
          aws_account_id: a.aws_account_id,
          name: a.name,
          access_key: isOrg || isRole ? null : a.access_key || null,
          secret_key: isOrg || isRole ? null : a.secret_key || null,
          session_key: isOrg || isRole ? null : a.session_key || null,
          role_arn: isRole ? a.role_arn.trim() || a.saved?.role_arn || null : null,
          // No external_id: the server always stores the company's own.
        })),
      }
      await client.saveAwsConnection(slug, payload)
      await onChanged()
      return true
    } catch (e) {
      setError(String(e))
      return false
    } finally {
      setBusy(false)
    }
  }, [slug, isOrg, isRole, draft, onChanged, client])

  useImperativeHandle(ref, () => ({ save }), [save])

  const setAccountTest = (idx: number, msg: string) => setAccountTests((t) => ({ ...t, [idx]: msg }))

  // Either/or testing: freshly typed keys are tested as-entered (pre-save);
  // with the fields left blank the SERVER decrypts and tests the saved pair —
  // the "do my stored keys still work?" case. Result labels say which.
  const testOrg = async () => {
    const entered = !!draft.org_access_key && !!draft.org_secret_key
    if (!entered && !connection?.has_org_keys) {
      setOrgTest('No saved org keys yet — enter access + secret keys to test.')
      return
    }
    setBusy(true)
    setOrgTest(entered ? 'Testing entered keys…' : 'Testing saved keys…')
    try {
      const r = entered
        ? await client.testAwsCredentials(slug, draft.org_access_key, draft.org_secret_key, undefined, draft.org_session_key || undefined)
        : await client.testAwsCredentials(slug, '', '')
      const label = entered ? '' : ' (saved keys)'
      setOrgTest(r.ok ? `OK${label} — account ${r.account_id} (${r.arn})` : `Failed${label} — ${r.reason}`)
    } catch (e) {
      setOrgTest(String(e))
    } finally {
      setBusy(false)
    }
  }

  const testRole = async (idx: number) => {
    const a = draft.accounts[idx]!
    const roleArn = a.role_arn.trim() || a.saved?.role_arn || ''
    if (!roleArn) {
      setAccountTest(idx, 'Enter the role ARN to test.')
      return
    }
    if (!client.testAwsAssumeRole) return
    setBusy(true)
    setAccountTest(idx, 'Assuming the role…')
    try {
      // The server assumes with the company's ExternalId and also proves the
      // role can NOT be assumed without it.
      const r = await client.testAwsAssumeRole(slug, roleArn, a.aws_account_id || undefined)
      if (r.ok && r.account_id) patchAccount(idx, { aws_account_id: r.account_id })
      setAccountTest(idx, r.ok ? `OK — assumed into ${r.account_id}` : `Failed — ${r.reason}`)
    } catch (e) {
      setAccountTest(idx, String(e))
    } finally {
      setBusy(false)
    }
  }

  const testAccount = async (idx: number) => {
    const a = draft.accounts[idx]!
    const entered = !!a.access_key && !!a.secret_key
    if (!entered && !a.saved?.has_keys) {
      setAccountTest(idx, 'No saved keys for this account — enter access + secret keys to test.')
      return
    }
    setBusy(true)
    setAccountTest(idx, entered ? 'Testing entered keys…' : 'Testing saved keys…')
    try {
      const r = entered
        ? await client.testAwsCredentials(slug, a.access_key, a.secret_key, a.aws_account_id || undefined, a.session_key || undefined)
        : await client.testAwsCredentials(slug, '', '', undefined, undefined, a.saved?.aws_account_id || a.aws_account_id || undefined)
      if (r.ok && r.account_id) patchAccount(idx, { aws_account_id: r.account_id })
      const label = entered ? '' : ' (saved keys)'
      setAccountTest(idx, r.ok ? `OK${label} — ${r.account_id}` : `Failed${label} — ${r.reason}`)
    } catch (e) {
      setAccountTest(idx, String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div data-testid="aws-connection-editor">
      <AwsAccessGuide />
      {error && <div className="error" role="alert">{error}</div>}

      {isSso && (
        <div style={{ marginBottom: '0.75rem' }}>
          {draft.accounts.length === 0 ? (
            <>
              <p style={{ color: 'var(--muted)', fontSize: '0.9rem', marginTop: 0 }}>
                Sign in through AWS IAM Identity Center (SSO). You can scan with the temporary
                credentials, or optionally authorize read-only AllCode access keys for future scans.
              </p>
              <AwsSsoConnectionWizard
                api={ssoApi}
                onResolvedAccounts={addSsoAccounts}
                autoOpenSignal={ssoAutoOpenSignal}
                hideTrigger={ssoAutoOpenSignal > 0}
              />
            </>
          ) : (
            <p style={{ color: 'var(--muted)', fontSize: '0.9rem', marginTop: 0 }} data-testid="aws-sso-connected-copy">
              SSO accounts are ready. Review the accounts below, then save the connection.
            </p>
          )}
        </div>
      )}

      {isOrg && (
        <>
          <AwsOrgIamSetupHelp />
          <div className="field-grid">
            <label>
              Org name (optional)
              <input type="text" value={draft.org_name} onChange={(e) => setDraft({ ...draft, org_name: e.target.value })} placeholder="BigCo" data-testid="aws-org-name-input" />
            </label>
            <label>
              Org AWS ID (optional)
              <input type="text" value={draft.org_aws_id} onChange={(e) => setDraft({ ...draft, org_aws_id: e.target.value })} placeholder="o-xxxxxxxxxx" />
            </label>
            <label>
              Org access key
              <input type="password" value={draft.org_access_key} onChange={(e) => setDraft({ ...draft, org_access_key: e.target.value })} placeholder={connection?.has_org_keys ? '(set — overwrite if needed)' : 'AKIA…'} data-testid="aws-access-key-input" autoComplete="off" />
            </label>
            <label>
              Org secret key
              <input type="password" value={draft.org_secret_key} onChange={(e) => setDraft({ ...draft, org_secret_key: e.target.value })} placeholder={connection?.has_org_keys ? '(set — overwrite if needed)' : 'wJal…'} data-testid="aws-secret-key-input" autoComplete="off" />
            </label>
            <label className="full">
              Org session token (optional — for temporary / SSO credentials)
              <input type="password" value={draft.org_session_key} onChange={(e) => setDraft({ ...draft, org_session_key: e.target.value })} placeholder="for ASIA… temporary keys" data-testid="aws-session-token-input" autoComplete="off" />
            </label>
          </div>
          <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button type="button" onClick={testOrg} disabled={busy} title="Tests the keys entered above — or the saved keys when the fields are left blank." data-testid="aws-test-creds-btn">Test credentials</button>
            <TestResult text={orgTest ?? undefined} size="0.85rem" testid="aws-test-result" />
          </div>
        </>
      )}

      {/* One ExternalId per company, so one snippet serves every role. */}
      {isRole && <AwsAssumeRoleSetupHelp identity={identity} />}
      {isRole && identity?.external_id && <ExternalIdField value={identity.external_id} />}

      {!isOrg && !isSso && !isRole && <AwsIamSetupHelp />}

      {/* Accounts (member accounts in org mode) */}
      <div style={{ marginTop: '1rem' }}>
        <strong>{isOrg ? 'Member accounts' : 'Accounts'}</strong>
        {draft.accounts.length === 0 && (
          <p style={{ color: 'var(--muted)', margin: '0.25rem 0' }}>
            {isSso ? 'Use the SSO sign-in above to add accounts.' : isOrg ? 'List the member account IDs to scrape.' : isRole ? 'Add the role ARN the customer sent you.' : 'Add an account and its read-only key.'}
          </p>
        )}
        {draft.accounts.map((a, idx) => (
          <div key={idx} className="aws-account-card" data-testid={`aws-account-row-${idx}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{isOrg ? 'Member account' : 'Account'} {idx + 1}{isSso && a.saved === undefined && a.access_key ? ' · ✓ credentials from SSO' : ''}</span>
              <button type="button" onClick={() => setDraft({ ...draft, accounts: draft.accounts.filter((_, i) => i !== idx) })} aria-label="remove account" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: '1rem', lineHeight: 1, padding: 0 }}>✕</button>
            </div>
            <div className="field-grid" style={{ marginTop: '0.4rem' }}>
              <label>
                {isOrg || isSso ? 'Account ID' : 'Account ID (optional)'}
                <input type="text" value={a.aws_account_id} onChange={(e) => patchAccount(idx, { aws_account_id: e.target.value })} placeholder={isOrg || isSso ? '123456789012' : '123456789012 (or Test to fill)'} maxLength={12} />
              </label>
              <label>
                Name
                <input type="text" value={a.name} onChange={(e) => patchAccount(idx, { name: e.target.value })} placeholder="Production" />
              </label>
              {isRole && (
                <label className="full">
                  Role ARN
                  <input type="text" value={a.role_arn} onChange={(e) => patchAccount(idx, { role_arn: e.target.value })} placeholder="arn:aws:iam::123456789012:role/ai-assessment-readonly" data-testid={`aws-account-role-arn-${idx}`} />
                </label>
              )}
              {!isOrg && !isSso && !isRole && (
                <>
                  <label>
                    Access key
                    <input type="password" value={a.access_key} onChange={(e) => patchAccount(idx, { access_key: e.target.value })} placeholder={a.saved?.has_keys ? '(set — overwrite)' : 'AKIA…'} autoComplete="off" data-testid={`aws-account-access-key-${idx}`} />
                  </label>
                  <label>
                    Secret key
                    <input type="password" value={a.secret_key} onChange={(e) => patchAccount(idx, { secret_key: e.target.value })} placeholder={a.saved?.has_keys ? '(set — overwrite)' : 'wJal…'} autoComplete="off" data-testid={`aws-account-secret-key-${idx}`} />
                  </label>
                  <label className="full">
                    Session token (optional — temporary / SSO creds)
                    <input type="password" value={a.session_key} onChange={(e) => patchAccount(idx, { session_key: e.target.value })} placeholder="for ASIA… temporary keys" autoComplete="off" data-testid={`aws-account-session-key-${idx}`} />
                  </label>
                </>
              )}
            </div>
            {isRole && (
              <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button type="button" onClick={() => testRole(idx)} disabled={busy} title="Assumes the role now, so a trust-policy mistake surfaces here rather than as a silently skipped account at scan time." data-testid={`aws-account-test-role-${idx}`}>Test role</button>
                <TestResult text={accountTests[idx]} size="0.8rem" testid={`aws-account-test-result-${idx}`} />
              </div>
            )}
            {!isOrg && !isSso && !isRole && (
              <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button type="button" onClick={() => testAccount(idx)} disabled={busy} title="Tests the keys entered above — or this account's saved keys when the fields are left blank." data-testid={`aws-account-test-${idx}`}>Test</button>
                <TestResult text={accountTests[idx]} size="0.8rem" />
              </div>
            )}
          </div>
        ))}
        {!isSso && (
          <button type="button" onClick={() => setDraft({ ...draft, accounts: [...draft.accounts, emptyAccount()] })} style={{ marginTop: '0.5rem' }} data-testid="aws-add-account-btn">
            + Add another {isOrg ? 'member account' : 'account'}
          </button>
        )}
      </div>
    </div>
  )
})
